// React Dependencies
import React, { useState, useEffect, useRef } from 'react';

// External Dependencies
import MaterialTable from 'material-table';
import { Link, Paper } from '@material-ui/core';
import { toast } from 'react-toastify';

// Internal Dependencies
import apiService from '../../../services.js/apiService';
import Select from '../../../components/common/filters/SelectFilter';
import InputFilter from '../../../components/common/filters/InputFilter';
import useDebounce from '../../../components/global/use-debounce';
import { AccountMaxPostPublished, BusinessPlan } from '../../../common/constants';

const PLAN_OPTS = [
  {
    label: BusinessPlan.Free,
    value: BusinessPlan.Free,
  },
  {
    label: BusinessPlan.Premium,
    value: BusinessPlan.Premium,
  },
  {
    label: BusinessPlan.Ultimate,
    value: BusinessPlan.Ultimate,
  },
];

function BusinessAccountsTable() {
  const tableRef = useRef(null);
  const [pageSize, setPageSize] = useState(20);
  const [filters, setFilters] = useState({});
  const [isLoading, setIsLoading] = useState(true);
  const debouncedFilters = useDebounce(filters, 500);

  const handleFilterSelect = (value, name) => {
    setFilters((prevState) => ({
      ...prevState,
      [name]: value ? value.value : null,
    }));
  };

  const handleFilter = (value, name) => {
    setFilters((prevState) => ({
      ...prevState,
      [name]: value || null,
    }));
  };

  useEffect(() => {
    if (tableRef.current) {
      tableRef.current.onQueryChange();
    }
  }, [debouncedFilters]);

  return (
    <>
      <div className="content-header mt-5">
        <h1>Business Accounts</h1>
      </div>

      <Paper>
        <div className="w-100 m-2 p-2 d-flex flex-wrap" style={{ gap: 5 }}>
          <div className="mx-1 my-1">
            <InputFilter
              value={filters.pageId}
              handleFilterChange={(value) => {
                handleFilter(value, 'pageId');
              }}
              placeholder="Page Id"
              id="pageId"
              width="180px"
              inputType="number"
            />
          </div>

          <div className="mx-1 my-1">
            <InputFilter
              value={filters.pageName}
              handleFilterChange={(value) => {
                handleFilter(value, 'pageName');
              }}
              placeholder="Page Name"
              id="pageName"
              width="180px"
            />
          </div>

          <div className="mx-1 my-1">
            <Select
              value={filters.plan}
              handleFilterChange={(value) => {
                handleFilterSelect(value, 'plan');
              }}
              options={PLAN_OPTS}
              placeholder="Account Plan"
              isClearable
              width="250px"
            />
          </div>
        </div>

        <MaterialTable
          tableRef={tableRef}
          columns={[
            {
              title:  'Page Id',
              field:  'pageId',
              render: (biz) => (
                <Link href={`page/${biz.pageId}`} target="_blank">
                  {biz.pageId}
                </Link>
              ),
            },
            {
              title: 'Page Name',
              field: 'pageName',
            },
            {
              title: 'Account Plan',
              field: 'plan',
            },
            {
              title:  'Monthly Live Post(s) Published / Limit',
              field:  'postsPublished',
              render: (biz) => (
                `${biz.postsPublished} / ${AccountMaxPostPublished[biz.plan]}`
              ),
            },
            {
              title: 'No. of Staff',
              field: 'noOfStaff',
            },
          ]}
          // eslint-disable-next-line no-async-promise-executor
          data={(query) => new Promise(async (resolve) => {
            try {
              setIsLoading(true);

              const data = await apiService.getBusinessAccounts({
                pageNo:   query.page,
                pageSize: query.pageSize,
                ...filters,
              });

              if (data) {
                resolve({
                  data:       data.rows || [],
                  page:       query.page || 0,
                  totalCount: data.totalCount || 0,
                });
              } else {
                resolve({
                  data:       [],
                  page:       0,
                  totalCount: 0,
                });
              }
            } catch (err) {
              toast.error('Error In Table Filter');
              resolve({
                data:       [],
                page:       0,
                totalCount: 0,
              });
            } finally {
              setIsLoading(false);
            }
          })}
          actions={[
            {
              icon:    'assignment',
              tooltip: 'View',
              onClick: (event, rowData) => {
                window.open(`/admin/business/${rowData.pageId}`);
              },
            },
          ]}
          title=""
          options={{
            pageSize,
            emptyRowsWhenPaging: false,
            actionsColumnIndex:  -1,
            search:              false,
            filtering:           false,
            minBodyHeight:       '400px',
            sorting:             false,
            debounceInterval:    500,
            pageSizeOptions:     [20, 40, 60, 80],
          }}
          onChangeRowsPerPage={() => {
            setPageSize(pageSize);
          }}
          isLoading={isLoading}
          components={{
            Container: (props) => <div {...props} />,
            Toolbar:   () => null,
          }}
        />
      </Paper>
    </>
  );
}

export default BusinessAccountsTable;
